import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  Box, 
  LinearProgress, 
  Typography, 
  Toolbar,
  Popover,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import { updatePdfPageDisplaySizes } from './pdfViewerLayout';
import { createPdfPageWindow } from './pdfPageWindow';
import useWheelZoom from './useWheelZoom';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { CloseButton, UnifiedAnswerRenderer } from '../Common';
import {
  VIEWER_TAG_HOVER_EVENT,
  buildTagHoverEventDetail,
  doesTagHoverDetailMatch,
  getTagHighlightBackgroundColor,
  getTagHighlightColor,
  getVisionOrientationByPage,
  padNormalizedBbox,
  rotateNormalizedBbox,
} from './TagHighlightUtils';
import { fetchViewerFile, useViewerFile } from './viewerHttp';
import { createRafResizeObserver } from '../../Utils/resizeObserver';
import {
  getNextPdfRotation,
  rotatePdfPageInMap,
  getPdfPageRotationState,
  normalizePdfRotation,
} from './pdfViewerRotation';
import {
  capturePdfScrollAnchor,
  restorePdfScrollAnchor,
} from './pdfViewerScroll';

// Theme colors
const primaryColor = '#5757b0';
const PDF_CANVAS_SCALE = 1.15;
const EXPECTED_PDF_RENDER_CANCELLATION_MESSAGES = [
  'Worker was terminated',
  'Worker was destroyed',
  'Page was destroyed',
  'Rendering cancelled',
];

const normalizeCoordinateItems = (coordinates) => {
  if (!coordinates) {
    return [];
  }

  let parsedCoordinates = coordinates;
  if (typeof coordinates === 'string') {
    try {
      parsedCoordinates = JSON.parse(coordinates);
    } catch (error) {
      return [];
    }
  }

  return Array.isArray(parsedCoordinates) ? parsedCoordinates : [parsedCoordinates];
};

const normalizeTagHighlightBoxes = (timeevent) => {
  const fields = Array.isArray(timeevent?.customFields) ? timeevent.customFields : [];
  const boxes = [];

  fields.forEach((field) => {
    const coordinateItems = normalizeCoordinateItems(field?.coordinates);
    const validCoordinateItems = coordinateItems.filter((coordinates) => {
      const bbox = coordinates?.bbox_percent;
      const page = Number(coordinates?.page || 1);
      if (!Array.isArray(bbox) || bbox.length !== 4 || !Number.isFinite(page) || page < 1) {
        return false;
      }

      const normalizedBbox = bbox.map((value) => Number(value));
      return !normalizedBbox.some((value) => !Number.isFinite(value));
    });

    validCoordinateItems.forEach((coordinates, occurrenceIndex) => {
      const bbox = coordinates?.bbox_percent;
      const page = Number(coordinates?.page || 1);
      if (!Array.isArray(bbox) || bbox.length !== 4 || !Number.isFinite(page) || page < 1) {
        return;
      }

      const normalizedBbox = bbox.map((value) => Number(value));
      if (normalizedBbox.some((value) => !Number.isFinite(value))) {
        return;
      }

      boxes.push({
        page,
        bbox: padNormalizedBbox(normalizedBbox) || normalizedBbox,
        label: field?.variable || '',
        field,
        occurrenceIndex,
      });
    });
  });

  return boxes;
};

const normalizeTagPopupCoordinates = (field) => normalizeCoordinateItems(field?.coordinates)
  .map((item) => {
    const bbox = item?.bbox_percent;
    const page = Number(item?.page || 1);
    if (!Array.isArray(bbox) || bbox.length !== 4 || !Number.isFinite(page)) {
      return null;
    }

    const normalizedBbox = bbox.map((value) => Number(value));
    if (normalizedBbox.some((value) => !Number.isFinite(value))) {
      return null;
    }

    return {
      page,
      bbox: normalizedBbox,
      text: item?.text || '',
    };
  })
  .filter(Boolean);

const formatCoordinateNumber = (value) => Number(value).toFixed(4);

const isExpectedPdfRenderCancellation = (error) => {
  const message = error?.message || String(error || '');

  return error?.name === 'AbortException' ||
    error?.name === 'RenderingCancelledException' ||
    EXPECTED_PDF_RENDER_CANCELLATION_MESSAGES.some((expectedMessage) => message.includes(expectedMessage));
};

const warnUnexpectedPdfLifecycleError = (message, error) => {
  if (!isExpectedPdfRenderCancellation(error)) {
    console.warn(message, error);
  }
};

const setTagHighlightState = (
  element,
  tagColor,
  { active = false, hidden = false, selected = false } = {},
) => {
  if (!element) {
    return;
  }

  element.style.opacity = hidden ? '0' : (selected ? '0.38' : (active ? '0.16' : '1'));
  element.style.borderWidth = selected ? '3px' : '2px';
  element.style.boxShadow = selected
    ? `0 0 0 5px ${tagColor}, 0 0 0 2px rgba(255,255,255,0.95) inset`
    : (active && !hidden
      ? `0 0 0 3px ${tagColor}, 0 0 0 1px rgba(255,255,255,0.85) inset`
      : 'none');
  element.style.zIndex = selected ? '4' : (active ? '2' : '1');
  element.style.pointerEvents = hidden ? 'none' : 'auto';
};

const updateHoveredTagHighlights = (
  container,
  detail,
  scrollToMatch = false,
  showTagHighlights = true,
) => {
  if (!container) {
    return;
  }

  const highlights = Array.from(container.querySelectorAll('[data-tag-highlight="true"]'))
    .map((element) => {
      const tag = {
        id: element.dataset.tagId || null,
        timeeventid: element.dataset.eventId || null,
        variable: element.dataset.tagVariable || '',
      };
      const tagColor = element.dataset.tagColor || getTagHighlightColor(tag.variable);

      return {
        element,
        tagColor,
        matches: doesTagHoverDetailMatch(detail, tag),
      };
    });

  if (!showTagHighlights) {
    highlights.forEach(({ element, tagColor }) => {
      setTagHighlightState(element, tagColor, { hidden: true });
    });
    return;
  }

  const hasHoveredMatch = Boolean(detail) && highlights.some((highlight) => highlight.matches);
  const shouldHideAllHighlights = Boolean(detail) && !hasHoveredMatch;
  const requestedOccurrence = Number(detail?.occurrenceIndex);
  const selectedHighlight = hasHoveredMatch && Number.isInteger(requestedOccurrence) && requestedOccurrence >= 0
    ? highlights.find((highlight) => highlight.matches
      && Number(highlight.element.dataset.tagOccurrenceIndex) === requestedOccurrence)
    : null;

  highlights.forEach(({ element, tagColor, matches }) => {
    setTagHighlightState(element, tagColor, {
      active: hasHoveredMatch && matches,
      selected: selectedHighlight?.element === element,
      hidden: shouldHideAllHighlights || (hasHoveredMatch && !matches),
    });
  });

  // A tag may belong to another PDF page. Bring its first matching evidence
  // box into view when the user hovers the tag in the validation panel.
  if (scrollToMatch && hasHoveredMatch && detail?.origin !== 'viewer') {
    const matchingHighlights = highlights.filter((highlight) => highlight.matches);
    const matchedHighlight = Number.isInteger(requestedOccurrence) && requestedOccurrence >= 0
      ? selectedHighlight || matchingHighlights[0]
      : matchingHighlights[0];
    matchedHighlight?.element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '90vw',
    height: '90vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const PDFContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  backgroundColor: '#f0f0f0',
  overflow: 'auto',
  // Keep the available page width stable when vertical scrolling starts or ends.
  // Without a reserved gutter, a page close to the viewport height can toggle
  // the scrollbar and make the fit-to-width calculation oscillate.
  scrollbarGutter: 'stable both-edges',
  '& canvas': {
    width: '100%',
    height: 'auto',
    display: 'block',
    backgroundColor: 'white',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    marginBottom: '16px',
  },
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(87, 87, 176, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(87, 87, 176, 0.4)',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.6)',
    },
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(87, 87, 176, 0.4) rgba(87, 87, 176, 0.1)',
}));

const PDFViewer = React.forwardRef(({ open, onClose, filename = '', eventId = null, filePath = '', timeevent = null, onTagHover = null, onTagHoverEnd = null, embedded = false, onPdfUrlChange = null, visiblePages = null, focusPage = null, focusPageIndex = null, focusPageRequest = 0, pageRotations = null, onPageRotate = null, pageRotationDisabled = false }, _ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    fileBlob: pdfBlob,
    fileUrl: pdfUrl,
    loading,
    progress,
    error,
  } = useViewerFile({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.pdf_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pdfRotation, setPdfRotation] = useState(0);
  const [localPageRotations, setPdfPageRotations] = useState({});
  const pdfPageRotations = pageRotations ?? localPageRotations;
  const [zoomScale, setZoomScale] = useState(1);
  const [pageRendering, setPageRendering] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [renderedPages, setRenderedPages] = useState(0);
  const [pageRenderError, setPageRenderError] = useState(null);
  const [selectedTagPopup, setSelectedTagPopup] = useState(null);
  const [showTagHighlights, setShowTagHighlights] = useState(true);
  const containerRef = useRef(null);
  const pagesContainerRef = useRef(null);
  const hoveredTagDetailRef = useRef(null);
  const pendingTagNavigationRef = useRef(null);
  const focusedPageRequestRef = useRef(null);
  const pageWindowRef = useRef(null);
  const pendingPdfScrollAnchorRef = useRef(null);
  const completedPdfScrollAnchorRef = useRef(null);
  const zoomScaleRef = useRef(zoomScale);
  const showTagHighlightsRef = useRef(showTagHighlights);
  zoomScaleRef.current = zoomScale;
  showTagHighlightsRef.current = showTagHighlights;
  const tagHighlightBoxes = React.useMemo(() => normalizeTagHighlightBoxes(timeevent), [timeevent]);
  const visionOrientationByPage = React.useMemo(
    () => getVisionOrientationByPage(timeevent),
    [timeevent],
  );
  const visiblePageSequence = React.useMemo(() => {
    // Split review passes an ordered source-page sequence here. Keep repeated
    // page numbers because they represent intentional output duplication.
    if (visiblePages == null) return null;
    return (Array.isArray(visiblePages) ? visiblePages : [])
      .map((page) => Number(page))
      .filter((page) => Number.isInteger(page) && page >= 1);
  }, [visiblePages]);

  useEffect(() => {
    if (typeof onPdfUrlChange !== 'function') return undefined;
    onPdfUrlChange(pdfUrl || null);
    return () => onPdfUrlChange(null);
  }, [onPdfUrlChange, pdfUrl]);

  useEffect(() => {
    focusedPageRequestRef.current = null;
    pendingPdfScrollAnchorRef.current = null;
    completedPdfScrollAnchorRef.current = null;
    setPdfPageRotations({});
  }, [pdfUrl]);

  useEffect(() => {
    const pageNumber = Number(focusPage);
    if (!open || !pdfUrl || !Number.isInteger(pageNumber) || pageNumber < 1) return undefined;
    if (!pagesContainerRef.current || focusedPageRequestRef.current === focusPageRequest) return undefined;

    const pageSequenceIndex = Number(focusPageIndex);
    const hasPageSequenceIndex = focusPageIndex != null
      && Number.isInteger(pageSequenceIndex)
      && pageSequenceIndex >= 0;
    const focusedPageWrapper = hasPageSequenceIndex
      ? pagesContainerRef.current.querySelector(
        `[data-pdf-page-wrapper="true"][data-pdf-page-sequence-index="${pageSequenceIndex}"]`,
      )
      : pagesContainerRef.current.querySelector(
        `[data-pdf-page-wrapper="true"][data-pdf-page-number="${pageNumber}"]`,
      );
    if (!focusedPageWrapper) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      focusedPageRequestRef.current = focusPageRequest;
      focusedPageWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [focusPage, focusPageIndex, focusPageRequest, open, pdfUrl, renderedPages, pageCount]);

  useWheelZoom({
    containerRef,
    enabled: Boolean(pdfUrl && !loading),
    maximumZoom: 3,
    minimumZoom: 0.5,
    setZoom: setZoomScale,
    zoom: zoomScale,
  });

  const rotatePdfPage = React.useCallback((pageNumber) => {
    const normalizedPageNumber = Number(pageNumber);
    if (pageRotationDisabled || !Number.isInteger(normalizedPageNumber) || normalizedPageNumber < 1) {
      return;
    }

    pendingPdfScrollAnchorRef.current = capturePdfScrollAnchor(containerRef.current);
    if (onPageRotate) {
      onPageRotate(normalizedPageNumber);
    } else {
      setPdfPageRotations((previous) => rotatePdfPageInMap(previous, normalizedPageNumber));
    }
  }, [onPageRotate, pageRotationDisabled]);

  useLayoutEffect(() => {
    updatePdfPageDisplaySizes(pagesContainerRef.current, zoomScale);
    pageWindowRef.current?.refresh();
  }, [zoomScale]);

  useLayoutEffect(() => {
    if (pageRendering || !completedPdfScrollAnchorRef.current) {
      return;
    }

    restorePdfScrollAnchor(containerRef.current, completedPdfScrollAnchorRef.current);
    completedPdfScrollAnchorRef.current = null;
  }, [pageRendering, renderedPages]);

  useEffect(() => {
    if (!open || !pdfUrl) {
      return undefined;
    }

    const pagesContainer = pagesContainerRef.current;
    const scrollContainer = containerRef.current;
    const updatePageSizes = () => {
      updatePdfPageDisplaySizes(pagesContainer, zoomScaleRef.current);
      pageWindowRef.current?.refresh();
    };

    updatePageSizes();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = createRafResizeObserver(updatePageSizes);
      // Page height changes when its width is updated. Observing the page
      // container therefore feeds the resize callback with its own writes and
      // can cause a fit-to-width loop for pages close to the viewport size.
      // The outer viewer is the stable sizing source we actually need.
      if (scrollContainer) {
        resizeObserver.observe(scrollContainer);
      }
      return () => resizeObserver.disconnect();
    }

    window.addEventListener('resize', updatePageSizes);
    return () => window.removeEventListener('resize', updatePageSizes);
  }, [open, pdfUrl]);

  useEffect(() => {
    if (typeof window === 'undefined' || !open) {
      return undefined;
    }

    const handleTagHover = (event) => {
      hoveredTagDetailRef.current = event?.detail || null;
      pendingTagNavigationRef.current = null;
      // Evidence on an unrendered page still has a lightweight navigation slot.
      const detail = hoveredTagDetailRef.current;
      if (detail && detail.origin !== 'viewer' && showTagHighlightsRef.current) {
        const matches = tagHighlightBoxes.filter((box) => doesTagHoverDetailMatch(
          detail, box.field || { variable: box.label }, timeevent,
        ));
        const occurrence = Number(detail.occurrenceIndex);
        const box = matches[Number.isInteger(occurrence) && occurrence >= 0 ? occurrence : 0] || matches[0];
        const wrapper = box && pagesContainerRef.current?.querySelector(
          `[data-pdf-page-wrapper="true"][data-pdf-page-number="${box.page}"]`,
        );
        if (wrapper && !wrapper.querySelector('canvas')) {
          pendingTagNavigationRef.current = box.page;
          wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      updateHoveredTagHighlights(
        pagesContainerRef.current,
        hoveredTagDetailRef.current,
        pendingTagNavigationRef.current == null,
        showTagHighlightsRef.current,
      );
    };

    window.addEventListener(VIEWER_TAG_HOVER_EVENT, handleTagHover);
    return () => {
      window.removeEventListener(VIEWER_TAG_HOVER_EVENT, handleTagHover);
      hoveredTagDetailRef.current = null;
      pendingTagNavigationRef.current = null;
    };
  }, [open, tagHighlightBoxes, timeevent]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    setShowTagHighlights(true);
    if (!open) {
      setPdfRotation(0);
      pendingPdfScrollAnchorRef.current = null;
      completedPdfScrollAnchorRef.current = null;
      setPdfPageRotations({});
      setPageRendering(false);
      setPageCount(0);
      setRenderedPages(0);
      setPageRenderError(null);
      setSelectedTagPopup(null);
    } else {
      setPdfRotation(0);
      pendingPdfScrollAnchorRef.current = null;
      completedPdfScrollAnchorRef.current = null;
      setPdfPageRotations({});
      setPageRendering(false);
      setPageCount(0);
      setRenderedPages(0);
      setPageRenderError(null);
      setSelectedTagPopup(null);
    }
  }, [open]);

  useEffect(() => {
    updateHoveredTagHighlights(
      pagesContainerRef.current,
      hoveredTagDetailRef.current,
      false,
      showTagHighlights,
    );
  }, [showTagHighlights]);

  useEffect(() => {
    if (!pdfUrl || !open) return;

    let cancelled = false;
    let pageWindow = null;
    let loadingTaskDestroyed = false;
    let loadingTask = null;
    const container = pagesContainerRef.current;
    const scrollAnchor = pendingPdfScrollAnchorRef.current
      || capturePdfScrollAnchor(containerRef.current);
    pendingPdfScrollAnchorRef.current = null;
    completedPdfScrollAnchorRef.current = null;
    focusedPageRequestRef.current = null;

    if (container) {
      container.innerHTML = '';
    }

    setPageRendering(true);
    setPageCount(0);
    setRenderedPages(0);
    setPageRenderError(null);

    const destroyLoadingTask = async () => {
      if (loadingTaskDestroyed) {
        return;
      }

      loadingTaskDestroyed = true;
      if (!loadingTask) {
        return;
      }
      try {
        await loadingTask.destroy();
      } catch (destroyError) {
        warnUnexpectedPdfLifecycleError('[PDFViewer] Failed to destroy PDF loading task:', destroyError);
      }
    };

    const renderPdfPages = async () => {
      try {
        const { GlobalWorkerOptions, getDocument } = await import('pdfjs-dist/build/pdf.mjs');
        if (cancelled) return;

        GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
        // Disable PDF JavaScript evaluation even for PDFs received from trusted URLs.
        loadingTask = getDocument({ url: pdfUrl, isEvalSupported: false });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        const pageNumbersToRender = visiblePageSequence
          ? visiblePageSequence.filter((pageNumber) => pageNumber <= pdf.numPages)
          : Array.from({ length: pdf.numPages }, (_, index) => index + 1);
        setPageCount(pageNumbersToRender.length);

        const renderPage = async (pageWrapper, signal) => {
          if (cancelled || signal.aborted) return;
          const pageNumber = Number(pageWrapper.dataset.pdfPageNumber);
          const pageSequenceIndex = Number(pageWrapper.dataset.pdfPageSequenceIndex);
          let currentRenderTask = null;
          const cancelRender = () => currentRenderTask?.cancel();
          const page = await pdf.getPage(pageNumber);
          signal.addEventListener('abort', cancelRender);
          try {
            if (cancelled || signal.aborted) return;
            const visionOrientation = visionOrientationByPage[pageNumber] || { rotation: 0, fineAngle: 0 };
            const pageOnlyRotation = normalizePdfRotation(pdfPageRotations[pageNumber]);
            const pageRotationState = getPdfPageRotationState({
              pdfPageRotation: page.rotate,
              visionRotation: visionOrientation.rotation,
              correctionRotation: visionOrientation.correctionRotation,
              metadataPdfRotation: visionOrientation.pdfRotation,
              finalRotation: visionOrientation.finalRotation,
              rotationBakedIntoPdf: visionOrientation.rotationBakedIntoPdf,
              viewerRotation: pdfRotation,
              pageOnlyRotation,
            });
            const pageRotation = pageRotationState.renderRotation;
            const viewport = page.getViewport({ scale: PDF_CANVAS_SCALE, rotation: pageRotation });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            if (!context) {
              throw new Error('Failed to render PDF page');
            }

            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.setAttribute('data-page-number', String(pageNumber));
            canvas.setAttribute('data-pdf-page-sequence-index', String(pageSequenceIndex));
            canvas.style.display = 'block';
            canvas.style.width = '100%';
            canvas.style.height = 'auto';
            canvas.style.marginBottom = '0';

            if (container) {
              const positionBeforeSizing = capturePdfScrollAnchor(containerRef.current);
              pageWrapper.style.position = 'relative';
              pageWrapper.dataset.pdfPageWrapper = 'true';
              pageWrapper.dataset.pdfNaturalWidth = String(viewport.width);
              pageWrapper.style.display = 'block';
              pageWrapper.style.maxWidth = 'none';
              pageWrapper.dataset.pdfPageNumber = String(pageNumber);
              pageWrapper.dataset.pdfPageSequenceIndex = String(pageSequenceIndex);
              pageWrapper.dataset.wheelZoomAnchor = `${pageNumber}:${pageSequenceIndex}`;
              pageWrapper.style.aspectRatio = `${viewport.width} / ${viewport.height}`;
              pageWrapper.style.margin = '0 auto 16px';
              pageWrapper.style.lineHeight = '0';
              const pageRotateButton = document.createElement('button');
              const pageRotateLabel = t(
                'viewer.rotate_pdf_page',
                'Rotate page {{page}}',
                { page: pageNumber },
              );
              pageRotateButton.type = 'button';
              pageRotateButton.setAttribute('aria-label', pageRotateLabel);
              pageRotateButton.title = onPageRotate
                ? t('viewer.rotate_pdf_split_page', { page: pageNumber })
                : pageRotateLabel;
              pageRotateButton.disabled = pageRotationDisabled;
              pageRotateButton.textContent = '↻';
              pageRotateButton.style.position = 'absolute';
              pageRotateButton.style.top = '8px';
              pageRotateButton.style.right = '8px';
              pageRotateButton.style.zIndex = '10';
              pageRotateButton.style.pointerEvents = 'auto';
              pageRotateButton.style.touchAction = 'manipulation';
              pageRotateButton.style.width = '32px';
              pageRotateButton.style.height = '32px';
              pageRotateButton.style.padding = '0';
              pageRotateButton.style.border = '1px solid rgba(87, 87, 176, 0.28)';
              pageRotateButton.style.borderRadius = '6px';
              pageRotateButton.style.backgroundColor = 'rgba(255, 255, 255, 0.92)';
              pageRotateButton.style.color = primaryColor;
              pageRotateButton.style.fontSize = '22px';
              pageRotateButton.style.lineHeight = '30px';
              pageRotateButton.style.cursor = 'pointer';
              pageRotateButton.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.16)';
              const stopPageRotatePropagation = (event) => {
                event.stopPropagation();
              };
              pageRotateButton.addEventListener('pointerdown', stopPageRotatePropagation);
              pageRotateButton.addEventListener('mousedown', stopPageRotatePropagation);
              pageRotateButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                rotatePdfPage(pageNumber);
              });
              pageRotateButton.addEventListener('mouseenter', () => {
                pageRotateButton.style.backgroundColor = '#ffffff';
              });
              pageRotateButton.addEventListener('mouseleave', () => {
                pageRotateButton.style.backgroundColor = 'rgba(255, 255, 255, 0.92)';
              });
              if (Math.abs(visionOrientation.fineAngle) >= 0.001) {
                // OpenCV's positive deskew angle is counter-clockwise; CSS uses the opposite visual direction.
                pageWrapper.style.transform = `rotate(${-visionOrientation.fineAngle}deg)`;
                pageWrapper.style.transformOrigin = 'center center';
              }
              pageWrapper.appendChild(canvas);

              const pageBoxes = tagHighlightBoxes.filter((box) => box.page === pageNumber);
              pageBoxes.forEach((box) => {
                const rotatedBbox = rotateNormalizedBbox(
                  box.bbox,
                  pageRotationState.bboxRotation,
                );
                if (!rotatedBbox) return;

                const [x0, y0, x1, y1] = rotatedBbox;
                const tagColor = getTagHighlightColor(box.label);
                const tagBackgroundColor = getTagHighlightBackgroundColor(box.label);
                const highlight = document.createElement('div');
                const hoverDetail = buildTagHoverEventDetail(box.field || { variable: box.label }, timeevent);
                highlight.title = box.label;
                highlight.dataset.tagHighlight = 'true';
                highlight.dataset.eventId = hoverDetail?.eventId == null ? '' : String(hoverDetail.eventId);
                highlight.dataset.tagId = hoverDetail?.tagId == null ? '' : String(hoverDetail.tagId);
                highlight.dataset.tagVariable = hoverDetail?.tagVariable || box.label || '';
                highlight.dataset.tagOccurrenceIndex = String(box.occurrenceIndex);
                highlight.dataset.tagColor = tagColor;
                highlight.style.position = 'absolute';
                highlight.style.left = `${Math.max(0, Math.min(1, x0)) * 100}%`;
                highlight.style.top = `${Math.max(0, Math.min(1, y0)) * 100}%`;
                highlight.style.width = `${Math.max(0, Math.min(1, x1) - Math.max(0, Math.min(1, x0))) * 100}%`;
                highlight.style.height = `${Math.max(0, Math.min(1, y1) - Math.max(0, Math.min(1, y0))) * 100}%`;
                highlight.style.border = `2px solid ${tagColor}`;
                highlight.style.backgroundColor = tagBackgroundColor;
                highlight.style.boxSizing = 'border-box';
                highlight.style.pointerEvents = 'auto';
                highlight.style.cursor = box.field ? 'pointer' : 'default';
                highlight.style.transition = 'opacity 120ms ease, box-shadow 120ms ease';
                highlight.setAttribute('role', box.field ? 'button' : 'presentation');
                if (box.field) {
                  highlight.setAttribute('tabindex', '0');
                  highlight.setAttribute('aria-label', `${t('timeevent.open_tag', 'Open tag')} ${box.label || ''}`.trim());
                }
                highlight.addEventListener('mouseenter', () => {
                  setTagHighlightState(highlight, tagColor, {
                    active: true,
                    hidden: !showTagHighlightsRef.current,
                  });
                  if (onTagHover) {
                    onTagHover(box.field, box.occurrenceIndex);
                  }
                });
                highlight.addEventListener('mouseleave', () => {
                  setTagHighlightState(highlight, tagColor, {
                    hidden: !showTagHighlightsRef.current,
                  });
                  if (onTagHoverEnd) {
                    onTagHoverEnd();
                  }
                });
                if (box.field) {
                  const openTag = (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedTagPopup({
                      anchorEl: highlight,
                      field: box.field,
                    });
                  };
                  highlight.addEventListener('click', openTag);
                  highlight.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      openTag(event);
                    }
                  });
                }

                if (box.label) {
                  const label = document.createElement('div');
                  label.textContent = box.label;
                  label.style.position = 'absolute';
                  label.style.top = '-2px';
                  label.style.right = '-2px';
                  label.style.maxWidth = '160px';
                  label.style.overflow = 'hidden';
                  label.style.textOverflow = 'ellipsis';
                  label.style.whiteSpace = 'nowrap';
                  label.style.backgroundColor = tagColor;
                  label.style.color = '#fff';
                  label.style.fontSize = '10px';
                  label.style.fontWeight = '700';
                  label.style.lineHeight = '14px';
                  label.style.padding = '1px 5px';
                  label.style.borderRadius = '0 0 0 3px';
                  label.style.boxSizing = 'border-box';
                  label.style.pointerEvents = 'none';
                  highlight.appendChild(label);
                }

                pageWrapper.appendChild(highlight);
              });
              // Keep the page action above canvases and tag highlight overlays.
              pageWrapper.appendChild(pageRotateButton);

              updatePdfPageDisplaySizes(container, zoomScaleRef.current);
              restorePdfScrollAnchor(containerRef.current, positionBeforeSizing);
              pageWindow?.refresh();
              updateHoveredTagHighlights(
                container,
                hoveredTagDetailRef.current,
                false,
                showTagHighlightsRef.current,
              );
            }

            currentRenderTask = page.render({ canvasContext: context, viewport });
            await currentRenderTask.promise;
          } finally {
            signal.removeEventListener('abort', cancelRender);
            currentRenderTask = null;
            page.cleanup();
          }

          if (cancelled || signal.aborted) return;
          if (pendingTagNavigationRef.current === pageNumber) {
            updateHoveredTagHighlights(
              container,
              hoveredTagDetailRef.current,
              true,
              showTagHighlightsRef.current,
            );
            pendingTagNavigationRef.current = null;
          }
          setRenderedPages((previousRenderedPages) => previousRenderedPages + 1);
        };

        // Reserve the document layout without allocating one bitmap per page.
        if (!container || !pageNumbersToRender.length) return;
        const firstPage = await pdf.getPage(pageNumbersToRender[0]);
        if (cancelled) {
          firstPage.cleanup();
          return;
        }
        const estimate = firstPage.getViewport({ scale: PDF_CANVAS_SCALE, rotation: 0 });
        firstPage.cleanup();
        const placeholders = pageNumbersToRender.map((pageNumber, pageSequenceIndex) => {
          const wrapper = document.createElement('div');
          const orientation = visionOrientationByPage[pageNumber] || {};
          const estimatedRotation = getPdfPageRotationState({
            pdfPageRotation: firstPage.rotate,
            visionRotation: orientation.rotation,
            correctionRotation: orientation.correctionRotation,
            metadataPdfRotation: orientation.pdfRotation,
            finalRotation: orientation.finalRotation,
            rotationBakedIntoPdf: orientation.rotationBakedIntoPdf,
            viewerRotation: pdfRotation,
            pageOnlyRotation: normalizePdfRotation(pdfPageRotations[pageNumber]),
          }).renderRotation;
          const sideways = estimatedRotation % 180 !== 0;
          const width = sideways ? estimate.height : estimate.width;
          const height = sideways ? estimate.width : estimate.height;
          wrapper.dataset.pdfPageWrapper = 'true';
          wrapper.dataset.pdfNaturalWidth = String(width);
          wrapper.dataset.pdfPageNumber = String(pageNumber);
          wrapper.dataset.pdfPageSequenceIndex = String(pageSequenceIndex);
          wrapper.dataset.wheelZoomAnchor = `${pageNumber}:${pageSequenceIndex}`;
          wrapper.style.cssText = 'position:relative;display:block;max-width:none;margin:0 auto 16px;line-height:0;background:white';
          wrapper.style.aspectRatio = `${width} / ${height}`;
          return wrapper;
        });
        const fragment = document.createDocumentFragment();
        placeholders.forEach((wrapper) => fragment.appendChild(wrapper));
        container.appendChild(fragment);
        updatePdfPageDisplaySizes(container, zoomScaleRef.current);
        restorePdfScrollAnchor(containerRef.current, scrollAnchor);
        pageWindow = createPdfPageWindow({
          root: containerRef.current,
          pages: placeholders,
          renderPage,
          onRelease: (wrapper) => {
            if (!cancelled) {
              setSelectedTagPopup((previous) => wrapper.contains(previous?.anchorEl) ? null : previous);
            }
          },
          onError: (err) => {
            if (cancelled || isExpectedPdfRenderCancellation(err)) return;
            console.error('Error rendering PDF page:', err);
            setPageRenderError(err?.message || 'Failed to render PDF');
          },
        });
        pageWindowRef.current = pageWindow;
      } catch (err) {
        if (cancelled || isExpectedPdfRenderCancellation(err)) return;
        console.error('Error rendering PDF pages:', err);
        setPageRenderError(err?.message || 'Failed to render PDF');
      } finally {
        if (!cancelled) {
          completedPdfScrollAnchorRef.current = scrollAnchor;
          setPageRendering(false);
        }
      }
    };

    const renderPromise = renderPdfPages();

    return () => {
      cancelled = true;
      const pendingRenders = pageWindow?.dispose();
      if (pageWindowRef.current === pageWindow) pageWindowRef.current = null;
      // Let pending PDF.js callbacks settle before terminating the worker. Destroying
      // mid-load can surface expected worker termination as a dev overlay error.
      Promise.all([renderPromise, pendingRenders])
        .catch((renderError) => {
          warnUnexpectedPdfLifecycleError('[PDFViewer] PDF render task failed during cleanup:', renderError);
        })
        .finally(() => {
          destroyLoadingTask();
        });
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [
    pdfUrl,
    open,
    pdfRotation,
    pdfPageRotations,
    tagHighlightBoxes,
    timeevent,
    t,
    onTagHover,
    onTagHoverEnd,
    visionOrientationByPage,
    rotatePdfPage,
    onPageRotate,
    pageRotationDisabled,
    visiblePageSequence,
  ]);

  const downloadPDF = async () => {
    if (!pdfBlob || isDownloading) return;

    setIsDownloading(true);
    let downloadBlob = pdfBlob;
    let downloadFilename = filename || 'document.pdf';
    try {
      const downloadedFile = await fetchViewerFile({
        eventId,
        filePath,
        download: true,
      });
      if (downloadedFile?.blob) {
        downloadBlob = downloadedFile.blob;
      }
      if (downloadedFile?.filename) {
        downloadFilename = downloadedFile.filename;
      }
    } catch (downloadError) {
      console.error('Unable to prepare the oriented PDF download:', downloadError);
    } finally {
      setIsDownloading(false);
    }

    const downloadUrl = URL.createObjectURL(downloadBlob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const rotatePDF = () => {
    pendingPdfScrollAnchorRef.current = capturePdfScrollAnchor(containerRef.current);
    setPdfRotation((previousRotation) => getNextPdfRotation(previousRotation));
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const selectedTagField = selectedTagPopup?.field || null;
  const selectedTagCoordinates = normalizeTagPopupCoordinates(selectedTagField);
  const ViewerRoot = embedded ? Box : StyledDialog;
  const viewerRootProps = embedded
    ? { sx: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' } }
    : { open, onClose, maxWidth: false, fullScreen: isFullscreen || isSmallScreen };

  return (
    <>
    <ViewerRoot {...viewerRootProps}>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <PictureAsPdfIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.pdf_viewer_title')} - ${filename}` : t('viewer.pdf_viewer_title')}
          </Typography>
        </Box>
        {!embedded && <CloseButton
          onClick={onClose}
          sx={{
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          size="small"
        />}
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadPDF} 
            disabled={!pdfBlob || isDownloading}
            size="small"
            title={t('viewer.download_pdf')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton 
            onClick={rotatePDF}
            disabled={!pdfUrl}
            size="small"
            title={t('viewer.rotate_pdf')}
          >
            <Rotate90DegreesCwIcon />
          </IconButton>
          <IconButton
            onClick={() => setZoomScale((current) => Math.max(0.5, Number((current - 0.25).toFixed(2))))}
            disabled={zoomScale <= 0.5}
            size="small"
            title="Zoom out"
          >
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="caption" sx={{ minWidth: 42, textAlign: 'center' }}>
            {Math.round(zoomScale * 100)}%
          </Typography>
          <IconButton
            onClick={() => setZoomScale((current) => Math.min(3, Number((current + 0.25).toFixed(2))))}
            disabled={zoomScale >= 3}
            size="small"
            title="Zoom in"
          >
            <ZoomInIcon />
          </IconButton>
          <FormControlLabel
            sx={{ ml: 1, mr: 0 }}
            control={(
              <Checkbox
                checked={showTagHighlights}
                onChange={(event) => setShowTagHighlights(event.target.checked)}
                size="small"
                disabled={tagHighlightBoxes.length === 0}
                inputProps={{ 'aria-label': t('viewer.show_pdf_tags', 'Show PDF tags') }}
              />
            )}
            label={t('viewer.show_pdf_tags', 'Show tags')}
          />
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mx: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('viewer.loading_pdf')} {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ flex: 1, maxWidth: 200 }}
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <PDFContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: 300, mb: 2 }}
              />
              <Typography color="text.secondary">
                {t('viewer.loading_pdf')} {Math.round(progress)}%
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_pdf')}
              </Typography>
              <Typography color="text.secondary">
                {error}
              </Typography>
            </Box>
          ) : pdfUrl ? (
            <Box sx={{ width: '100%', height: '100%', p: 2, flexShrink: 0 }}>
              {pageRendering && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('viewer.rendering_pdf')} {renderedPages}/{pageCount || 0}
                  </Typography>
                  <LinearProgress
                    variant={pageCount ? 'determinate' : 'indeterminate'}
                    value={pageCount ? (renderedPages / pageCount) * 100 : undefined}
                  />
                </Box>
              )}
              {pageRenderError ? (
                <Typography color="error">
                  {pageRenderError}
                </Typography>
              ) : (
                <Box
                  ref={pagesContainerRef}
                  sx={{ width: '100%', minWidth: '100%' }}
                />
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No PDF content available
            </Typography>
          )}
        </PDFContainer>
      </ViewerDialogContent>
    </ViewerRoot>
    <Popover
      open={Boolean(selectedTagPopup?.anchorEl && selectedTagField)}
      anchorEl={selectedTagPopup?.anchorEl || null}
      onClose={() => setSelectedTagPopup(null)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 'min(900px, calc(100vw - 24px))',
          maxWidth: 'min(900px, calc(100vw - 24px))',
          maxHeight: 'min(84vh, 760px)',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          borderRadius: '12px',
          border: `2px solid ${primaryColor}20`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'min(84vh, 760px)' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2.5,
            py: 1.5,
            pb: 1,
            flexShrink: 0,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            borderBottom: `2px solid ${primaryColor}15`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 700,
              color: primaryColor,
              fontSize: '0.9rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedTagField?.variable || ''}
          </Typography>
          <CloseButton
            size="small"
            onClick={() => setSelectedTagPopup(null)}
            sx={{ color: primaryColor }}
          />
        </Box>
        <Box sx={{ p: 2, overflow: 'auto' }}>
          <UnifiedAnswerRenderer
            tag={selectedTagField}
            value={selectedTagField?.reply ?? ''}
            widgetId={`pdfviewer-tag-preview-${selectedTagField?.id || selectedTagField?.variable || 'tag'}`}
            disableInternalScroll={true}
          />
          {selectedTagCoordinates.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid rgba(87, 87, 176, 0.18)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.75,
                  color: primaryColor,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {t('timeevent.tag_coordinates', 'Coordinates')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {selectedTagCoordinates.map((item, index) => (
                  <Box
                    key={`${item.page}-${index}`}
                    sx={{
                      p: 1,
                      borderRadius: '8px',
                      backgroundColor: 'rgba(244, 67, 54, 0.06)',
                      border: '1px solid rgba(244, 67, 54, 0.22)',
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', color: '#333', fontWeight: 600 }}>
                      {t('timeevent.tag_coordinates_page', 'Page {{page}}', { page: item.page })}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#555',
                        fontFamily: 'monospace',
                        wordBreak: 'break-word',
                      }}
                    >
                      [{item.bbox.map(formatCoordinateNumber).join(', ')}]
                    </Typography>
                    {item.text && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: '#777',
                          wordBreak: 'break-word',
                        }}
                      >
                        {t('timeevent.tag_coordinates_text', 'Matched text')}: {item.text}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Popover>
    </>
  );
});

// Set display name for debugging
PDFViewer.displayName = 'PDFViewer';

export default PDFViewer;
